import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(undefined);

// Create axios instance with interceptor for auth
const api = axios.create({
  baseURL: API,
  withCredentials: true
});

// Add auth token to all requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('session_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('session_token');
    if (!token) {
      setLoading(false);
      return;
    }
    
    try {
      const response = await api.get('/api/auth/me');
      setUser(response.data);
    } catch (error) {
      localStorage.removeItem('session_token');
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await api.post('/api/auth/login', { email, password });
    localStorage.setItem('session_token', response.data.access_token);
    setUser(response.data.user);
    return response.data.user;
  };

  const register = async (email, password, name) => {
    const response = await api.post('/api/auth/register', { email, password, name });
    localStorage.setItem('session_token', response.data.access_token);
    setUser(response.data.user);
    return response.data.user;
  };

  const processGoogleCode = async (code) => {
    const redirectUri = window.location.origin + '/auth/callback';
    const response = await api.post('/api/auth/google/callback', { code, redirect_uri: redirectUri });
    localStorage.setItem('session_token', response.data.access_token);
    setUser(response.data.user);
    return response.data.user;
  };

  const processSsoSession = async (sessionId) => {
    // FoundryAI SSO hub landed us on /?session_id=xxx — exchange for a local session token.
    const response = await api.post('/api/auth/sso/callback', { session_id: sessionId });
    const token = response.data.token || response.data.access_token;
    if (!token) throw new Error('No session token returned from SSO callback');
    localStorage.setItem('session_token', token);
    // Fetch the user row (foundry_sso returns limited fields; /auth/me is the source of truth)
    const me = await api.get('/api/auth/me');
    setUser(me.data);
    return me.data;
  };

  const logout = async () => {
    try {
      await api.post('/api/auth/logout', {});
    } catch (error) {
      console.error('Logout error:', error);
    }
    localStorage.removeItem('session_token');
    setUser(null);
  };

  const getAuthHeader = () => {
    const token = localStorage.getItem('session_token');
    return {
      headers: { Authorization: `Bearer ${token}` },
      withCredentials: true
    };
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      login, 
      register, 
      logout, 
      processGoogleCode,
      processSsoSession,
      getAuthHeader,
      checkAuth,
      api
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
